import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  GripVertical,
  Maximize2,
  Plus,
  X,
} from 'lucide-react';
import {
  type BoardView,
  type ProjectMemberView,
  type TaskPriority,
  type TaskView,
  type WorkflowStatusView,
  entersWork,
} from '@apm/contracts';
import { StoryPointBadge } from './story-point-badge';
import { Avatar } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { DatePicker } from '../../../components/ui/date-picker';
import { ProgressQuickAction } from '../../../components/ui/progress-quick-action';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { cn } from '../../../lib/utils';
import { errorMessage } from '../../../lib/error-message';
import { boardApi, boardKeys } from '../../board/api';
import { priorityToneOf, statusTone } from '../../board/status-color';
import {
  type BoardFilter,
  NO_BOARD_FILTER,
  isFilterActive,
  matchesFilter,
} from '../../board/filter';
import { taskFormApi, taskFormKeys } from '../api';
import { AssigneeMenu } from './assignee-picker';
import {
  EMPTY_FILTER,
  GROUP_BYS,
  type GroupBy,
  type TaskGroup,
  type ListFilter,
  type ListSort,
  columnsForTask,
  filterTasks,
  flatten,
  groupTasks,
  hasBothColumnScopes,
  isOverdue,
  sortTasks,
} from '../task-list';
import { isOffPlan } from '../off-plan';
import { displayDate } from '../../../lib/date';
import { completedOn, isCompletedLate, todayIso } from '../../../lib/overdue';
import { RecurringMark } from './recurring-mark';
import { canMoveBetween } from '../status-flags';
import { isTooSoonToComplete, useCompleteTaskGuard } from '../use-complete-task-guard';

/** Cột của bảng, theo đúng thứ tự hiện trên màn hình. */
const COLUMNS: readonly { sort: ListSort; align?: 'right' | 'center' }[] = [
  { sort: 'key' },
  { sort: 'title' },
  { sort: 'assignee', align: 'center' },
  { sort: 'status', align: 'center' },
  { sort: 'due' },
  { sort: 'timeline' },
  { sort: 'progress' },
  { sort: 'priority', align: 'center' },
  { sort: 'points', align: 'right' },
];

const PRIORITIES: readonly TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

/**
 * Cột của board, phẳng ra.
 *
 * `BoardView.columns` là `{ status, tasks, count, overWipLimit }` — gói cột
 * **kèm việc trong nó**, vì board vẽ theo cột. Bảng thì chỉ cần phần `status`:
 * nó vẽ theo hàng và đã có danh sách việc của riêng mình.
 */
function columnsOf(board: BoardView | undefined): readonly WorkflowStatusView[] {
  return (board?.columns ?? []).map((column) => column.status);
}

/**
 * Cách nhìn **Danh sách** — APM-BOARD-b, dựng lại giao diện ở APM-LIST-a.
 *
 * ## Vì sao đọc lại `GET /board` chứ không có endpoint riêng
 *
 * Board, Gantt và Danh sách là ba cách vẽ **cùng một** tập cột và task. Một
 * endpoint riêng cho bảng này là mở đường cho ba màn hình nói ba chuyện khác
 * nhau về cùng một sprint — và lệch kiểu đó không ai thấy cho tới lúc hai người
 * cãi nhau xem con số nào đúng.
 *
 * ## Vì sao nó tồn tại, khi đã có Board
 *
 * Board trả lời "công việc đang chảy thế nào". Bảng trả lời "mọi việc quá hạn
 * của Bảo" — một câu hỏi về **hàng**, không về bức tranh. Trên board thì câu ấy
 * là quét mắt qua bốn cột và tự nhớ.
 *
 * ## Ô trạng thái và ưu tiên **tô kín màu** — APM-LIST-a
 *
 * Đây là thứ đổi nhiều nhất so với bản trước, và nó không phải chuyện trang
 * trí: bảng này được đọc bằng cách **quét dọc** một cột để tìm chỗ bất thường.
 * Một cột toàn chữ xám cùng cỡ thì không quét được — mắt phải dừng lại đọc
 * từng ô. Một dải màu thì bắt được ngay ở tầm nhìn ngoại vi.
 *
 * Chữ vẫn nằm trong ô vì màu một mình không đọc được với người mù màu, và màu
 * chữ do `statusTone` quyết chứ không do người đặt màu cột — xem lý do ở đó.
 *
 * ## Sửa tại chỗ, không phải mở việc ra mới sửa được
 *
 * Bấm vào ô trạng thái hay ô ưu tiên là đổi được ngay. Đó là lý do chính người
 * ta mở cách nhìn này thay vì board: đi qua hai mươi việc và dọn lại trạng
 * thái của chúng, mà mở popup từng cái là hai mươi lần mở-đóng.
 *
 * ⚠️ Mỗi ô một cửa quyền, cùng cửa mà API chặn: trạng thái và tiến độ theo
 * `canEdit`; hạn, thời gian và ưu tiên theo `canSchedule` (PM); người phụ trách
 * theo `canAssign`. Không có quyền thì ô vẫn tô màu nhưng không bấm được — một
 * ô bấm được rồi nhận 403 tệ hơn một ô không bấm được.
 */
export function TaskListView({
  projectId,
  sprintId,
  all = false,
  period = null,
  filter: shared = NO_BOARD_FILTER,
  canEdit = false,
  canSchedule = false,
  canAssign = false,
  openedTaskId = null,
  onOpen,
}: {
  readonly projectId: string;
  readonly sprintId?: string | undefined;
  readonly all?: boolean;
  /** Kỳ của dự án bảo trì — APM-MAINT-e. Bỏ trống = không lọc theo thời gian. */
  readonly period?: string | null;
  /**
   * Bộ lọc/làm nổi dùng chung với Board và Gantt — APM-BOARD-h.
   *
   * Thay ba ô chọn cũ của riêng bảng này (người, trạng thái, quá hạn): chúng
   * hỏi gần đúng những câu mà thanh chung đã hỏi, chỉ bằng một giao diện khác
   * — nên người dùng phải học hai lần và đặt lại mỗi khi đổi cách nhìn.
   *
   * Ô tìm kiếm theo chữ **ở lại**: thanh chung không có nó, và một bảng hai
   * trăm dòng không tìm được theo tên là một bảng phải cuộn.
   */
  readonly filter?: BoardFilter;
  /** `task:update` trong dự án này — quyết định ô nào sửa tại chỗ được. */
  readonly canEdit?: boolean;
  /**
   * `project:update` (PM) — mở ô Hạn, Timeline và Ưu tiên. Xem
   * `assertMaySchedule` ở API. Hạn / Timeline còn mở theo cờ `canSchedule` của từng việc
   * (cài đặt "Quyền đổi ngày" của dự án — APM-TSCH-b).
   */
  readonly canSchedule?: boolean;
  /** `task:assign` — bấm vào avatar ở cột Phụ trách để đổi người. */
  readonly canAssign?: boolean;
  /** Việc đang mở popup — hàng của nó giữ dấu để người dùng không lạc chỗ. */
  readonly openedTaskId?: string | null;
  readonly onOpen: (taskId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ListFilter>(EMPTY_FILTER);
  const [sort, setSort] = useState<ListSort>('key');
  const [descending, setDescending] = useState(false);
  /** Việc đang được tick — dùng cho thanh hành động hàng loạt ở đáy. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('status');
  /** Việc đang bị nhấc — để `DragOverlay` vẽ bản bay theo con trỏ. */
  const [dragging, setDragging] = useState<TaskView | null>(null);
  /** Khoá của những nhóm đang gập lại. */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const today = todayIso();
  const windowQuery = {
    mode: 'list' as const,
    limit: 200,
    sort,
    descending: descending ? ('1' as const) : ('0' as const),
    text: filter.text,
    includeBacklog: !all ? ('1' as const) : ('0' as const),
    today,
    ...(filter.assigneeId !== null
      ? { assigneeId: filter.assigneeId || 'unassigned' }
      : shared.mode === 'filter' && shared.assigneeId
        ? { assigneeId: shared.assigneeId }
        : {}),
    ...(shared.mode === 'filter' && shared.taskTypeId ? { taskTypeId: shared.taskTypeId } : {}),
    ...(filter.statusId ? { statusId: filter.statusId } : {}),
    ...(filter.overdueOnly
      ? { due: 'overdue' as const }
      : shared.mode === 'filter' && shared.due
        ? { due: shared.due }
        : {}),
  };
  const windowScope = JSON.stringify([projectId, sprintId, all, period, windowQuery]);
  const [pageCursor, setPageCursor] = useState({ scope: '', offset: 0 });
  const offset = pageCursor.scope === windowScope ? pageCursor.offset : 0;
  const board = useQuery({
    queryKey: [...boardKeys.board(projectId, sprintId, period), 'list-window', windowQuery, offset],
    queryFn: ({ signal }) =>
      boardApi.window(projectId, sprintId, all, period, { ...windowQuery, offset }, signal),
  });

  /**
   * Phải rê 6px mới tính là kéo — nếu không, mọi cú bấm vào tay nắm đều khởi
   * động một lượt kéo và ô tick cạnh nó thành thứ khó bấm trúng.
   */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /** Làm mới cả tiền tố: đổi một việc lệch cả board, backlog lẫn số đếm. */
  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['board', projectId], exact: false }).then();

  const moveStatus = useMutation({
    // Vị trí 0: việc vừa đổi trạng thái lên đầu cột mới. Giữ nguyên thứ tự cũ
    // thì nó rơi vào giữa một cột người dùng không nhìn, và cú bấm trông như
    // không có tác dụng.
    mutationFn: ({
      taskId,
      statusId,
      actualStartAt,
    }: {
      taskId: string;
      statusId: string;
      actualStartAt?: string;
    }) => boardApi.changeStatus(taskId, statusId, 0, actualStartAt),
    onError: (error, variables) => {
      // Chưa đủ thời gian tối thiểu (APM-TMIN-b) ⇒ hộp khai giờ, khai xong thì gửi lại.
      const task = rows.find((row) => row.id === variables.taskId);
      if (
        complete.handleError(
          error,
          task ?? { title: '' },
          (actualStartAt) => moveStatus.mutate({ ...variables, actualStartAt }),
          () => moveStatus.mutate(variables),
        )
      ) {
        return;
      }
      toast.error(errorMessage(error, t));
    },
    onSettled: invalidate,
  });

  /**
   * Sửa ngày ngay trong bảng — APM-LIST-d.
   *
   * `dueDate` và `startDate` đi chung một lời gọi: ô Timeline sửa cả hai cùng
   * lúc, và hai PATCH liên tiếp cho một thao tác là hai dòng `event_log` cho
   * một lần người dùng kéo một dải.
   */
  const setDates = useMutation({
    mutationFn: ({
      taskId,
      ...dates
    }: {
      taskId: string;
      startDate?: string | null;
      dueDate?: string | null;
    }) => taskFormApi.update(taskId, dates),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: invalidate,
  });

  const setProgress = useMutation({
    mutationFn: ({
      taskId,
      progressPct,
      actualStartAt,
    }: {
      taskId: string;
      progressPct: number;
      actualStartAt?: string;
    }) => boardApi.setProgress(taskId, progressPct, actualStartAt),
    onError: (error, variables) => {
      const task = rows.find((row) => row.id === variables.taskId);
      if (
        complete.handleError(
          error,
          task ?? { title: '' },
          (actualStartAt) => setProgress.mutate({ ...variables, actualStartAt }),
          () => setProgress.mutate(variables),
        )
      ) {
        return;
      }
      toast.error(errorMessage(error, t));
    },
    onSettled: invalidate,
  });

  /*
    Việc thành xong (100%, đổi sang cột nhóm Hoàn thành) qua hai lời hỏi: thời
    gian tối thiểu của dự án (APM-TMIN-b) và đồng hồ bấm giờ đang chạy
    (APM-TIME-done).
  */
  const complete = useCompleteTaskGuard();

  /**
   * Loại việc, để hàng "Thêm việc" có cái mà gửi — APM-LIST-e.
   *
   * `CreateTaskInput` bắt buộc `taskTypeId`. Hàng thêm nhanh cố ý **không**
   * hỏi loại: nó tồn tại để gõ một cái tên rồi Enter, và một ô chọn nữa là
   * đúng thứ biến nó trở lại thành cái form mà nó thay thế. Ai cần loại khác
   * thì mở việc ra sửa — một cú bấm, ngay sau khi tạo.
   *
   * Cùng `queryKey` với `TaskFormDialog` nên gần như luôn lấy từ cache.
   */
  const taskTypes = useQuery({
    queryKey: taskFormKeys.taskTypes(projectId),
    queryFn: () => taskFormApi.taskTypes(projectId),
    staleTime: 5 * 60_000,
    enabled: canEdit,
  });

  const createTask = useMutation({
    mutationFn: (input: { title: string; statusId?: string; assigneeId?: string }) =>
      taskFormApi.create(projectId, { ...input, taskTypeId: taskTypes.data![0]!.id }),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: invalidate,
  });

  const setPriority = useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: TaskPriority }) =>
      taskFormApi.update(taskId, { priority }),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: invalidate,
  });

  /** Cùng khoá với `TaskFormDialog` — mở form sau đó là lấy từ cache. */
  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
    staleTime: 5 * 60_000,
    enabled: canAssign,
  });

  /**
   * Đổi người phụ trách ngay trong bảng — đường riêng `PATCH /tasks/:id/assignee`
   * như form (xem `taskFormApi.assign`), vì nó trả cảnh báo quá tải/vắng mặt
   * phải hiện ra.
   */
  const assign = useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string | null }) =>
      taskFormApi.assign(taskId, assigneeId),
    onSuccess: (result) => {
      for (const warning of result.warnings) toast.warning(warning.message);
      toast.success(t('taskForm.assigned'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: invalidate,
  });

  const rows = useMemo(() => {
    if (!board.data) return [];
    const tasks = flatten(board.data);
    /*
      Hai tầng lọc, và chúng **không** trùng nhau — APM-BOARD-h.

      `filterTasks` nay chỉ còn làm mỗi việc tìm theo chữ (ba trục kia đã bỏ),
      còn `matchesFilter` là thanh chung. Ở chế độ "làm nổi" thì thanh chung
      **không** cắt hàng nào: nó chỉ làm chìm — cùng hành vi với board, nơi thẻ
      không khớp vẫn đứng nguyên chỗ để thấy ngữ cảnh xung quanh.
    */
    const shown =
      shared.mode === 'filter' ? tasks.filter((task) => matchesFilter(task, shared, today)) : tasks;
    return sortTasks(filterTasks(shown, filter, today), sort, descending);
  }, [board.data, filter, shared, sort, descending, today]);

  /** Hàng không khớp thanh chung ở chế độ "làm nổi" — làm chìm, không giấu. */
  const dimmed = useMemo(
    () =>
      shared.mode === 'highlight' && isFilterActive(shared)
        ? new Set(rows.filter((task) => !matchesFilter(task, shared, today)).map((task) => task.id))
        : new Set<string>(),
    [rows, shared, today],
  );

  /** Cột tra theo id — ô trạng thái cần `color` và `category`, `TaskView` không mang. */
  const statuses = useMemo(
    () => new Map(columnsOf(board.data).map((status) => [status.id, status])),
    [board.data],
  );

  if (board.isPending) return <Skeleton className="h-64" />;
  if (board.isError) {
    return (
      <ErrorState message={errorMessage(board.error, t)} onRetry={() => void board.refetch()} />
    );
  }

  // Cùng tập với `rows` trước khi lọc — kể cả việc chưa xếp lịch. Lấy thiếu ở
  // đây thì con số "hiện/tổng" đọc thành `3/2`.
  const everyTask = flatten(board.data);

  const toggleSort = (next: ListSort): void => {
    if (next === sort) setDescending((value) => !value);
    else {
      setSort(next);
      setDescending(false);
    }
  };

  const togglePick = (taskId: string): void =>
    setPicked((current) => {
      const next = new Set(current);
      if (!next.delete(taskId)) next.add(taskId);
      return next;
    });

  const groups = groupTasks(
    rows,
    groupBy,
    columnsOf(board.data),
    t('taskList.unassigned'),
    t('taskList.allRows'),
  );

  /** Board có cả cột của sprint lẫn cột ngoài sprint ⇒ tên cột trùng nhau, phải gắn nhãn. */
  const bothScopes = hasBothColumnScopes(columnsOf(board.data));

  /** Số cột thật của bảng — hàng tiêu đề nhóm và hàng tổng kết phải trải hết. */
  const span = COLUMNS.length + (canEdit ? 1 : 0);

  /** Chỉ những việc **đang hiện**: tick rồi lọc mất thì nó không còn là lựa chọn. */
  const pickedRows = rows.filter((task) => picked.has(task.id));
  /** Dòng tick được — việc của người khác thì không có ô tick (APM-TASK-own). */
  const pickable = rows.filter((task) => task.canEdit ?? true);
  const allPicked = pickable.length > 0 && pickedRows.length === pickable.length;

  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={filter.text}
          onChange={(event) => setFilter((current) => ({ ...current, text: event.target.value }))}
          placeholder={t('taskList.searchPlaceholder')}
          aria-label={t('taskList.searchPlaceholder')}
          className="h-8 w-56 rounded-control border border-border-strong bg-background px-2 text-sm"
        />

        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {t('taskList.groupBy')}
          <select
            value={groupBy}
            onChange={(event) => setGroupBy(event.target.value as GroupBy)}
            className="h-8 rounded-control border border-border-strong bg-background px-2 text-xs"
          >
            {GROUP_BYS.map((option) => (
              <option key={option} value={option}>
                {t(`taskList.group.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {t('taskList.count', {
            shown: rows.length,
            total: board.data.page?.total ?? everyTask.length,
          })}
        </span>
      </div>

      {board.data.page && (
        <div className="flex items-center gap-2 text-xs">
          <Button
            size="sm"
            variant="outline"
            disabled={board.data.page.offset === 0}
            onClick={() => {
              setPageCursor({
                scope: windowScope,
                offset: Math.max(0, board.data!.page!.offset - 200),
              });
              setPicked(new Set());
            }}
          >
            {t('common.previous')}
          </Button>
          <span>
            {board.data.page.offset + (rows.length ? 1 : 0)}–{board.data.page.offset + rows.length}{' '}
            / {board.data.page.total}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={board.data.page.offset + 200 >= board.data.page.total}
            onClick={() => {
              setPageCursor({ scope: windowScope, offset: board.data!.page!.offset + 200 });
              setPicked(new Set());
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title={t('taskList.emptyTitle')}
          description={
            everyTask.length === 0 ? t('board.emptyDescription') : t('taskList.emptyFiltered')
          }
        />
      ) : (
        /*
          Kéo một hàng = **đổi cột**, không phải đổi thứ tự — APM-LIST-c.

          Bảng đang sắp theo cột người dùng chọn (mã, hạn, tiến độ…), nên một cú
          kéo đổi thứ tự sẽ đánh nhau với chính phép sắp xếp ấy: thả xong là nó
          nhảy về chỗ cũ. Đích thả vì thế là **nhóm**, đúng như kéo thẻ giữa hai
          cột trên board — và vì vậy nó chỉ bật khi đang gom theo cột.
        */
        <DndContext
          sensors={sensors}
          onDragStart={(event: DragStartEvent) =>
            setDragging(rows.find((task) => task.id === event.active.id) ?? null)
          }
          onDragCancel={() => setDragging(null)}
          onDragEnd={(event: DragEndEvent) => {
            setDragging(null);
            const statusId = event.over?.id;
            if (typeof statusId !== 'string' || statusId === '') return;
            const task = rows.find((row) => row.id === event.active.id);
            // Thả lại vào đúng nhóm cũ là một cú kéo không có ý định nào —
            // gửi đi thì nó vẫn là một event trong `event_log`.
            if (!task || task.statusId === statusId) return;
            const toCategory = statuses.get(statusId)?.category ?? '';
            complete.guard(
              task,
              toCategory === 'done',
              (actualStartAt) =>
                moveStatus.mutate({
                  taskId: task.id,
                  statusId,
                  ...(actualStartAt ? { actualStartAt } : {}),
                }),
              undefined,
              entersWork(task.statusCategory, toCategory),
            );
          }}
        >
          <div className="overflow-x-auto rounded-DEFAULT border border-border">
            <table className="w-full min-w-[64rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  {canEdit ? (
                    <th scope="col" className="w-9 px-2">
                      <input
                        type="checkbox"
                        className="ios-check ios-check-select"
                        aria-label={t('taskList.pickAll')}
                        checked={allPicked}
                        onChange={() =>
                          setPicked(
                            allPicked ? new Set() : new Set(pickable.map((task) => task.id)),
                          )
                        }
                      />
                    </th>
                  ) : null}

                  {COLUMNS.map((column) => (
                    <th
                      key={column.sort}
                      scope="col"
                      className={cn(
                        'p-2 font-normal',
                        column.align === 'right' && 'text-right',
                        column.align === 'center' && 'text-center',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(column.sort)}
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        aria-label={t('taskList.sortBy', {
                          column: t(`taskList.column.${column.sort}`),
                        })}
                      >
                        {t(`taskList.column.${column.sort}`)}
                        {sort === column.sort ? (
                          descending ? (
                            <ArrowDown aria-hidden className="size-3" />
                          ) : (
                            <ArrowUp aria-hidden className="size-3" />
                          )
                        ) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>

              {groups.map((group) => (
                <GroupBody
                  key={group.key}
                  group={group}
                  tone={
                    groupBy === 'status' && statuses.has(group.key)
                      ? statusTone(statuses.get(group.key)!)
                      : null
                  }
                  /* Hai nhóm cùng tên "Đang làm" (của sprint và ngoài sprint) đứng
                     cạnh nhau thì phải nói cái nào là cái nào. */
                  hint={
                    groupBy === 'status' && bothScopes && statuses.get(group.key)?.sprintId === null
                      ? t('board.outsideZone')
                      : null
                  }
                  span={span}
                  collapsed={collapsed.has(group.key)}
                  onToggle={() =>
                    setCollapsed((current) => {
                      const next = new Set(current);
                      if (!next.delete(group.key)) next.add(group.key);
                      return next;
                    })
                  }
                  /* Thả vào nhóm = đổi cột. Chỉ có nghĩa khi đang gom theo cột —
                   xem `GroupBody`. */
                  droppable={canSchedule && groupBy === 'status'}
                  /*
                  Hàng thêm nhanh chỉ hiện khi dự án đã có loại việc: thiếu nó
                  thì mọi lần Enter là một 400, và lỗi ấy không nói được cho
                  người dùng biết phải làm gì.
                */
                  onCreate={
                    canEdit && (taskTypes.data?.length ?? 0) > 0
                      ? (title) =>
                          createTask.mutate({
                            title,
                            /* Nhóm đang đứng quyết định giá trị mặc định — thêm
                             việc vào "Đang làm" thì nó phải ở "Đang làm", chứ
                             không rơi vào cột mặc định rồi nhảy đi chỗ khác
                             ngay trước mắt người vừa tạo. */
                            ...(groupBy === 'status' && group.key !== ''
                              ? { statusId: group.key }
                              : {}),
                            ...(groupBy === 'assignee' && group.key !== ''
                              ? { assigneeId: group.key }
                              : {}),
                          })
                      : null
                  }
                  renderRow={(task) => (
                    <Row
                      key={task.id}
                      task={task}
                      status={statuses.get(task.statusId) ?? null}
                      columns={columnsOf(board.data)}
                      // Tên sprint cho tiêu đề nhóm trong menu cột — chỉ biết khi
                      // board đang xem đúng sprint chứa việc này.
                      sprintName={
                        board.data?.sprint && board.data.sprint.id === task.sprintId
                          ? board.data.sprint.name
                          : null
                      }
                      today={today}
                      /* Quyền cấp dự án **và** quyền trên đúng dòng này —
                         APM-TASK-own. */
                      canEdit={canEdit && (task.canEdit ?? true)}
                      pickColumn={canEdit}
                      // Đổi ngày là lập kế hoạch — chỉ PM (APM-CHANGE-b); server
                      // cũng chặn ở `PATCH /tasks/:id`.
                      canSchedule={canSchedule}
                      canAssign={canAssign}
                      members={members.data ?? []}
                      /* Kéo hàng chỉ dành cho PM (người dùng chốt 2026-09-25) — và
                         cùng một điều kiện cho **mọi** hàng: tay nắm chiếm chỗ
                         trong ô Mã, nên hàng có hàng không là mã lệch nhau. */
                      draggable={canSchedule && groupBy === 'status'}
                      // Chìm, không giấu: ở chế độ "làm nổi" hàng không khớp vẫn ở
                      // nguyên chỗ để thấy nó đứng cạnh những việc nào.
                      dimmed={dimmed.has(task.id)}
                      opened={task.id === openedTaskId}
                      picked={picked.has(task.id)}
                      onPick={() => togglePick(task.id)}
                      onOpen={() => onOpen(task.id)}
                      onStatus={(statusId) =>
                        complete.guard(
                          task,
                          statuses.get(statusId)?.category === 'done',
                          (actualStartAt) =>
                            moveStatus.mutate({
                              taskId: task.id,
                              statusId,
                              ...(actualStartAt ? { actualStartAt } : {}),
                            }),
                        )
                      }
                      onPriority={(priority) => setPriority.mutate({ taskId: task.id, priority })}
                      onAssign={(assigneeId) => assign.mutate({ taskId: task.id, assigneeId })}
                      onDates={(dates) => setDates.mutate({ taskId: task.id, ...dates })}
                      onProgress={(progressPct) =>
                        complete.guard(task, progressPct >= 100, (actualStartAt) =>
                          setProgress.mutate({
                            taskId: task.id,
                            progressPct,
                            ...(actualStartAt ? { actualStartAt } : {}),
                          }),
                        )
                      }
                    />
                  )}
                />
              ))}
            </table>
          </div>

          {/* Bản rõ nét bay theo con trỏ. Gọn lại còn mã + tên: kéo cả một hàng
            tám cột đi qua màn hình thì che mất chính chỗ sắp thả vào. */}
          <DragOverlay>
            {dragging ? (
              <span className="flex items-center gap-2 rounded-control border border-border-strong bg-background px-2 py-1 text-xs shadow-lg">
                <span className="font-mono text-muted-foreground">{dragging.key}</span>
                <span className="max-w-64 truncate">{dragging.title}</span>
              </span>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/*
        Thanh hành động hàng loạt — APM-LIST-a.

        Nổi ở đáy vùng nội dung chứ không chèn thêm một hàng vào bảng: nó xuất
        hiện và biến mất theo lựa chọn, và một khối chèn giữa trang sẽ đẩy mọi
        hàng nhảy chỗ đúng lúc người dùng đang tick.
      */}
      {complete.dialog}

      {pickedRows.length > 0 ? (
        <BulkBar
          count={pickedRows.length}
          /* Chỉ cột **mọi** việc đang tick cùng tới được: cột ngoài sprint, và
             cột của sprint khi cả lô nằm chung một sprint. */
          columns={columnsOf(board.data).filter(
            (column) =>
              column.sprintId === null ||
              pickedRows.every((task) => task.sprintId === column.sprintId),
          )}
          outsideLabel={bothScopes ? t('board.outsideZone') : null}
          sprintLabel={t('board.sprintFrameAny')}
          onStatus={(statusId) => {
            const completes = statuses.get(statusId)?.category === 'done';
            /*
              Việc chưa đủ thời gian tối thiểu (APM-TMIN-b) đứng lại: khai giờ bắt
              đầu là chuyện của từng việc, một hộp cho cả lô thì không biết khai
              cho việc nào. Báo số việc bị giữ lại, phần còn lại vẫn đi.
            */
            const held = completes ? pickedRows.filter(isTooSoonToComplete) : [];
            if (held.length > 0) {
              toast.warning(t('taskMinDuration.bulkHeld', { count: held.length }));
            }
            /*
              Thiếu hạn / ước lượng (APM-EREF-c) cũng đứng lại, cùng lý do: popup điền
              nhanh là chuyện của từng việc.
            */
            const toCategory = statuses.get(statusId)?.category ?? '';
            const unplanned = pickedRows.filter(
              (task) =>
                !held.includes(task) &&
                entersWork(task.statusCategory, toCategory) &&
                (task.planMissing?.length ?? 0) > 0,
            );
            if (unplanned.length > 0) {
              toast.warning(t('taskPlanGate.bulkHeld', { count: unplanned.length }));
            }
            held.push(...unplanned);
            // Mỗi người một đồng hồ ⇒ nhiều nhất một việc trong lô phải hỏi.
            for (const task of pickedRows) {
              if (held.includes(task)) continue;
              complete.guard(task, completes, () =>
                moveStatus.mutate({ taskId: task.id, statusId }),
              );
            }
            setPicked(new Set());
          }}
          onClear={() => setPicked(new Set())}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Một nhóm của bảng — APM-LIST-c.
 *
 * ## Tiêu đề nhóm là một hàng của **chính bảng**, không phải một bảng riêng
 *
 * Mỗi nhóm một `<table>` thì bề rộng cột do nội dung từng nhóm quyết, và hai
 * nhóm cạnh nhau sẽ lệch cột — đúng thứ phá tan khả năng quét dọc mà cả thiết
 * kế này dựng nên. Một bảng, nhiều `<tbody>`, là HTML hợp lệ và giữ cột thẳng.
 *
 * ## Nhóm rỗng vẫn hiện khi gom theo cột
 *
 * "Chờ duyệt trống" khác hẳn "Chờ duyệt không tồn tại" — xem `groupTasks`.
 *
 * ## Không có hàng tổng kết dưới nhóm
 *
 * Người dùng bỏ đi 2026-09-25: số việc đã có ở tiêu đề nhóm, và một dòng số
 * đếm + thanh phân bố ngay dưới "Thêm việc" chỉ làm mỗi nhóm cao thêm một hàng
 * mà không nói điều gì mới.
 */
function GroupBody({
  group,
  tone,
  hint = null,
  span,
  collapsed,
  droppable,
  onCreate,
  onToggle,
  renderRow,
}: {
  readonly group: TaskGroup;
  /** Màu của cột, khi nhóm **là** một cột. `null` thì dùng màu trung tính. */
  readonly tone: { background: string; foreground: string } | null;
  /** Nhãn phụ cạnh tên nhóm — "Cột ngoài sprint" khi board có hai bộ cột cùng tên. */
  readonly hint?: string | null;
  readonly span: number;
  readonly collapsed: boolean;
  readonly droppable: boolean;
  /** `null` = không thêm được (thiếu quyền, hoặc dự án chưa có loại việc). */
  readonly onCreate: ((title: string) => void) | null;
  readonly onToggle: () => void;
  readonly renderRow: (task: TaskView) => ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const drop = useDroppable({ id: group.key, disabled: !droppable });

  return (
    /*
      Đích thả phải **nhìn thấy được**, không chỉ một lớp nền nhạt — APM-LIST-c.

      Kéo một hàng đi qua bảng thì con trỏ lướt qua nhiều nhóm, và một sắc nền
      5% không đủ để người ta biết mình đang ở đâu **trước khi** nhả tay. Thả
      nhầm nhóm là đổi trạng thái một việc thật, và đường lùi là phải nhớ trạng
      thái cũ. Viền trong đậm + nền rõ nói ra điều đó ở tầm nhìn ngoại vi.
    */
    <tbody
      ref={drop.setNodeRef}
      className={cn(drop.isOver && 'bg-primary/10 ring-2 ring-inset ring-primary')}
    >
      <tr className="border-b border-border bg-muted/20">
        <td colSpan={span} className="p-0">
          {/* Thanh màu dọc ở mép trái cả khối — thứ nói "những hàng này thuộc
              về nhau" mà không tốn một chữ nào. */}
          <span
            className="flex items-center gap-2 border-l-4 px-2 py-1.5"
            style={{ borderColor: tone?.background ?? 'var(--color-border-strong)' }}
          >
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              className="flex items-center gap-1.5 text-sm font-medium hover:underline"
            >
              {collapsed ? (
                <ChevronRight aria-hidden className="size-4" />
              ) : (
                <ChevronDown aria-hidden className="size-4" />
              )}
              {group.label}
            </button>
            {hint ? (
              <span className="rounded-DEFAULT bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {hint}
              </span>
            ) : null}
            <span className="text-xs tabular-nums text-muted-foreground">
              {t('taskList.groupCount', { count: group.tasks.length })}
            </span>
          </span>
        </td>
      </tr>

      {collapsed ? null : group.tasks.map((task) => renderRow(task))}

      {/* Nhóm rỗng phải nói ra bằng chữ: một tiêu đề nhóm rồi tới ngay tiêu đề
          nhóm sau đọc như bảng bị lỗi. Nhưng khi thêm được việc thì hàng
          "Thêm việc" ngay dưới đã nói điều đó rõ hơn, và hai dòng chồng nhau
          chỉ làm nhóm rỗng cao gấp đôi nhóm có một việc. */}
      {!collapsed && group.tasks.length === 0 && onCreate === null ? (
        <tr className="border-b border-border/60">
          <td colSpan={span} className="px-3 py-2 text-xs text-muted-foreground">
            {t('taskList.groupEmpty')}
          </td>
        </tr>
      ) : null}

      {!collapsed && onCreate ? (
        <tr className="border-b border-border/60">
          <td colSpan={span} className="p-0">
            <AddRow onCreate={onCreate} />
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

/**
 * Hàng "Thêm việc" ở cuối mỗi nhóm — APM-LIST-e.
 *
 * ## Vì sao ngay trong bảng, khi đã có nút "+ Việc mới"
 *
 * Nút ấy mở một hộp thoại hỏi loại, người phụ trách, ngày, ước lượng — đúng
 * việc cần làm khi tạo **một** việc đã nghĩ kỹ. Nhưng lúc dựng kế hoạch thì
 * người ta gõ ra mười cái tên liền một mạch rồi mới quay lại điền chi tiết, và
 * mười lần mở-đóng hộp thoại giết đúng cái mạch ấy.
 *
 * Nên hàng này cố ý **chỉ hỏi tên**. Thêm một ô nữa là biến nó trở lại thành
 * cái form mà nó thay thế.
 *
 * ## Nhóm đang đứng là giá trị mặc định
 *
 * Gõ vào nhóm "Đang làm" thì việc mới ở "Đang làm". Cho nó rơi vào cột mặc
 * định của dự án là để người vừa tạo nhìn thấy việc của mình nhảy sang chỗ
 * khác ngay trước mắt.
 *
 * ## Enter giữ ô mở, Escape đóng, rời ô thì **không mất chữ**
 *
 * Enter tạo xong và ở lại để gõ tiếp — đó là cả lý do hàng này tồn tại. Rời ô
 * khi còn chữ thì tạo luôn thay vì im lặng vứt đi: người dùng đã gõ, và một ô
 * tự xoá nội dung khi mất focus là cách nhanh nhất làm họ mất niềm tin vào
 * việc gõ trực tiếp vào bảng.
 */
function AddRow({ onCreate }: { readonly onCreate: (title: string) => void }): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');

  const submit = (): void => {
    const next = title.trim();
    setTitle('');
    if (next !== '') onCreate(next);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        <Plus aria-hidden className="size-3.5" />
        {t('taskList.addTask')}
      </button>
    );
  }

  return (
    <input
      autoFocus
      value={title}
      placeholder={t('taskList.addTaskPlaceholder')}
      aria-label={t('taskList.addTask')}
      onChange={(event) => setTitle(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
        if (event.key === 'Escape') {
          setTitle('');
          setOpen(false);
        }
      }}
      onBlur={() => {
        submit();
        setOpen(false);
      }}
      className="w-full border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Row({
  task,
  status,
  columns,
  sprintName = null,
  today,
  canEdit,
  pickColumn,
  canSchedule,
  canAssign,
  members,
  draggable = false,
  dimmed = false,
  opened = false,
  picked = false,
  onPick,
  onOpen,
  onStatus,
  onPriority,
  onAssign,
  onDates,
  onProgress,
}: {
  readonly task: TaskView;
  /** Cột chứa việc này. `null` khi việc ở backlog (không thuộc cột nào). */
  readonly status: WorkflowStatusView | null;
  readonly columns: readonly WorkflowStatusView[];
  /** Tên sprint chứa việc này, khi board biết — cho tiêu đề nhóm của menu cột. */
  readonly sprintName?: string | null;
  readonly today: string;
  readonly canEdit: boolean;
  /**
   * Bảng có cột tick không — quyền **cấp dự án**, cùng điều kiện với `<th>` ở
   * tiêu đề. Tách khỏi `canEdit` (quyền trên đúng dòng này): gộp lại thì dòng
   * không sửa được **mất hẳn ô đầu**, mọi ô sau lệch sang trái một cột, và dòng
   * của chính người xem trông như bị thụt vào.
   */
  readonly pickColumn: boolean;
  readonly canSchedule: boolean;
  readonly canAssign: boolean;
  /** Thành viên dự án — chỉ nạp khi `canAssign`. */
  readonly members: readonly ProjectMemberView[];
  /** Kéo được sang nhóm khác — chỉ khi đang gom theo cột (APM-LIST-c). */
  readonly draggable?: boolean;
  /** Không khớp thanh lọc ở chế độ "làm nổi" — APM-BOARD-h. */
  readonly dimmed?: boolean;
  /** Đang mở popup của chính việc này. */
  readonly opened?: boolean;
  readonly picked?: boolean;
  readonly onPick: () => void;
  readonly onOpen: () => void;
  readonly onStatus: (statusId: string) => void;
  readonly onPriority: (priority: TaskPriority) => void;
  /** `null` = bỏ giao. */
  readonly onAssign: (assigneeId: string | null) => void;
  readonly onDates: (dates: { startDate?: string | null; dueDate?: string | null }) => void;
  readonly onProgress: (progressPct: number) => void;
}): ReactNode {
  const { t } = useTranslation();
  /*
    Cột việc này tới được — của đúng sprint chứa nó, cộng cột ngoài sprint. Có
    cả hai bộ thì menu chia nhóm có tiêu đề: hai bộ mặc định trùng tên nhau.
  */
  const reachable = columnsForTask(columns, task);
  const grouped = hasBothColumnScopes(reachable);
  const late = isOverdue(task, today);
  const done = task.statusCategory === 'done';
  /** Xong nhưng xong sau hạn — vẫn phải thấy được dù việc đã đóng (người dùng yêu cầu 2026-09-30). */
  const doneLate = isCompletedLate(task.dueDate, task.statusCategory, task.actualEndAt);
  const priority = priorityToneOf(task.priority);
  const drag = useDraggable({ id: task.id, disabled: !draggable });
  // Ngày mở theo cài đặt của dự án (APM-TSCH-b, cờ `canSchedule` của từng việc); ưu tiên vẫn PM.
  const datesEditable = canSchedule || task.canSchedule === true;

  return (
    <tr
      ref={drag.setNodeRef}
      className={cn(
        // `group` để những thứ chỉ-hiện-khi-hover bên trong bắt được trạng thái
        // của cả hàng: bảng lúc nghỉ phải sạch, nút chỉ mọc ra khi tay tới gần.
        'group border-b border-border/60 last:border-b-0',
        // Hàng đang bị nhấc: chìm hẳn tại chỗ cũ, bản rõ nét bay theo con trỏ
        // ở `DragOverlay`. Ẩn hẳn thì bảng co lại và mọi hàng dưới nhảy chỗ.
        drag.isDragging && 'opacity-40',
        opened
          ? // Hàng đang mở popup giữ dấu suốt lúc popup còn mở — không thì đóng
            // lại là mất chỗ, và ở một bảng hai trăm dòng thì mất hẳn.
            'bg-primary/10'
          : picked
            ? // Đã tick: nền **và** vạch trái. Chỉ nền thì ở một bảng đã có hai
              // cột tô kín màu, một sắc 5% biến mất hẳn — và người dùng mất dấu
              // chính những hàng họ sắp đổi hàng loạt.
              'bg-primary/10 shadow-[inset_3px_0_0_0_var(--color-primary)]'
            : 'hover:bg-muted/40',
        // `opacity` chứ không đổi màu chữ: hàng có mã, tên, người, ngày, điểm —
        // làm nhạt từng thứ một là năm lớp màu phải giữ cho khớp nhau.
        dimmed && 'opacity-35',
      )}
    >
      {pickColumn ? (
        <td className="px-2">
          {canEdit ? (
            <input
              type="checkbox"
              className="ios-check ios-check-select"
              checked={picked}
              onChange={onPick}
              aria-label={t('taskList.pick', { key: task.key })}
            />
          ) : null}
        </td>
      ) : null}

      <td className="whitespace-nowrap p-2 font-mono text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          {/* Tay nắm kéo mọc ra khi hover, ở lề trái — cùng chỗ mọi bảng dữ
              liệu đặt nó. Kéo bằng tay nắm chứ không bằng cả hàng: hàng có ô
              tick, có nút, có hai ô bấm-để-sửa, và một cú kéo bắt đầu từ bất
              cứ đâu sẽ nuốt mất tất cả. */}
          {draggable ? (
            <button
              type="button"
              aria-label={t('taskList.drag', { key: task.key })}
              className="cursor-grab text-muted-foreground opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
              {...drag.listeners}
              {...drag.attributes}
            >
              <GripVertical aria-hidden className="size-3.5" />
            </button>
          ) : null}
          {task.key}
        </span>
      </td>

      <td className="p-2">
        <span className="flex items-center gap-2">
          <button type="button" onClick={onOpen} className="text-left hover:underline">
            {task.title}
          </button>

          {task.recurrenceId ? <RecurringMark /> : null}

          {/* Việc ngoài kế hoạch PM — APM-TASK-creator. Xem `isOffPlan`. */}
          {isOffPlan(task) ? (
            <span
              aria-label={t('board.createdByOtherUnknown')}
              title={
                task.createdByName === null
                  ? t('board.createdByOtherUnknown')
                  : t('board.createdByOtherTitle', { name: task.createdByName })
              }
              className="inline-block size-1.5 shrink-0 rounded-full bg-rag-amber"
            />
          ) : null}

          {/* Nút mở chi tiết chỉ mọc ra khi hover — cùng chỗ, cùng cử chỉ với
              bấm vào tên, nhưng nó là thứ **nhìn thấy được**: bấm vào tên là
              quy ước phải biết trước. `focus` để bàn phím cũng thấy nó. */}
          <button
            type="button"
            onClick={onOpen}
            aria-label={t('taskList.open', { key: task.key })}
            className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100"
          >
            <Maximize2 aria-hidden className="size-3.5" />
          </button>
        </span>
      </td>

      <td className="p-2 text-center">
        <QuickEdit
          editable={canAssign}
          label={t('taskList.changeAssignee', { key: task.key })}
          width="w-64"
          content={<Avatar name={task.assigneeName} size="sm" />}
        >
          {(close) => (
            <AssigneeMenu
              members={members}
              current={task.assigneeId}
              onPick={(assigneeId) => {
                if (assigneeId !== task.assigneeId) onAssign(assigneeId);
                close();
              }}
            />
          )}
        </QuickEdit>
      </td>

      {/* `p-0`: ô tô kín thì màu phải chạm mép ô, không để một viền trắng quanh
          nó — viền ấy biến một dải màu liền mạch thành một dãy nhãn rời. */}
      <td className="p-0">
        <FillCell
          tone={
            status
              ? statusTone(status)
              : // Việc ở backlog không thuộc cột nào: vẫn tô, bằng tông "chưa
                // làm", để cột không thủng một lỗ trắng giữa chừng.
                statusTone({ id: task.statusId, category: task.statusCategory, color: null })
          }
          label={task.statusName}
          /*
            Đổi cột theo rule dự án (APM-TSP-d), không theo quyền sửa dòng: rule
            mặc định cho thành viên đổi trạng thái cả việc họ không sửa được.
            Cờ vắng mặt thì rơi về `canEdit` như trước.
          */
          editable={
            (task.canMove ?? canEdit) &&
            canMoveBetween(task, task.statusCategory, task.statusCategory)
          }
          menuLabel={t('taskList.changeStatus')}
          options={reachable
            .filter((column) => canMoveBetween(task, task.statusCategory, column.category))
            .map((column) => ({
              value: column.id,
              label: column.name,
              tone: statusTone(column),
              // Hai bộ cột cùng tên ⇒ tiêu đề nhóm, cùng chữ với hai khung của board.
              ...(grouped
                ? {
                    group:
                      column.sprintId === null
                        ? t('board.outsideZone')
                        : sprintName
                          ? t('board.sprintFrame', { name: sprintName })
                          : t('board.sprintFrameAny'),
                  }
                : {}),
            }))}
          current={task.statusId}
          onPick={onStatus}
        />
      </td>

      {/* Quá hạn tô đỏ **và** kèm icon: màu một mình không đọc được với người
          mù màu, và cột này là chỗ duy nhất thông tin ấy xuất hiện. */}
      <td className="whitespace-nowrap p-2 text-xs tabular-nums">
        <QuickEdit
          editable={datesEditable}
          label={t('taskList.changeDue', { key: task.key })}
          content={
            task.dueDate === null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5',
                  (late || doneLate) && 'font-semibold text-rag-red',
                )}
                {...(doneLate
                  ? {
                      title: t('taskList.doneLateTitle', {
                        done: displayDate(completedOn(task.statusCategory, task.actualEndAt)),
                        due: displayDate(task.dueDate),
                      }),
                    }
                  : {})}
              >
                {done ? (
                  <Check aria-hidden className="size-3.5 text-rag-green" />
                ) : late ? (
                  <AlertCircle aria-hidden className="size-3.5" />
                ) : (
                  <CircleDashed aria-hidden className="size-3.5 text-muted-foreground" />
                )}
                {/* Gạch ngang khi đã xong: một cái hạn của việc đã đóng vẫn là
                    dữ kiện, nhưng nó thôi là việc phải làm. Xong **trễ** thì
                    không gạch: đỏ đậm là cả thông tin, và gạch đi thì nó chìm
                    vào đống hạn xám của những việc xong đúng hạn. */}
                <span className={cn(done && !doneLate && 'text-muted-foreground line-through')}>
                  {displayDate(task.dueDate)}
                </span>
                {late ? <span className="sr-only">{t('taskList.overdue')}</span> : null}
                {doneLate ? (
                  <span className="rounded-control bg-rag-red/10 px-1 text-[10px] leading-4">
                    {t('taskList.doneLate')}
                  </span>
                ) : null}
              </span>
            )
          }
        >
          {(close) => (
            <span className="flex flex-col gap-1.5">
              <DatePicker
                className="w-full"
                aria-label={t('taskList.column.due')}
                value={task.dueDate ?? ''}
                onChange={(next) => {
                  onDates({ dueDate: next === '' ? null : next });
                  close();
                }}
              />
              {task.dueDate === null ? null : (
                <button
                  type="button"
                  className="text-left text-xs text-muted-foreground hover:text-rag-red"
                  onClick={() => {
                    onDates({ dueDate: null });
                    close();
                  }}
                >
                  {t('taskList.clearDate')}
                </button>
              )}
            </span>
          )}
        </QuickEdit>
      </td>

      {/*
        Timeline — APM-LIST-d.

        Ô duy nhất trong bảng sửa được **ngày bắt đầu**. Nó cố ý chồng lên cột
        Hạn ở phần `dueDate`: hai cột trả lời hai câu khác nhau — Hạn nói "có
        trễ không", Timeline nói "việc chạy trong khoảng nào" — và đó cũng là
        cách monday xếp hai cột ấy cạnh nhau.
      */}
      <td className="whitespace-nowrap p-2 text-xs tabular-nums">
        <QuickEdit
          editable={datesEditable}
          label={t('taskList.changeTimeline', { key: task.key })}
          content={
            task.startDate === null && task.dueDate === null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              // Viên pill, không phải hai ngày rời: một dải thời gian đọc ra là
              // một vật, và hai con số cách nhau bằng dấu gạch thì mắt phải tự
              // ghép lại.
              <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                {task.startDate === null ? '…' : displayDate(task.startDate)} →{' '}
                {task.dueDate === null ? '…' : displayDate(task.dueDate)}
              </span>
            )
          }
        >
          {(close) => (
            <span className="flex flex-col gap-1.5">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('taskList.startDate')}
                <DatePicker
                  className="w-full"
                  value={task.startDate ?? ''}
                  onChange={(next) => onDates({ startDate: next === '' ? null : next })}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('taskList.column.due')}
                <DatePicker
                  className="w-full"
                  value={task.dueDate ?? ''}
                  onChange={(next) => {
                    onDates({ dueDate: next === '' ? null : next });
                    close();
                  }}
                />
              </label>
            </span>
          )}
        </QuickEdit>
      </td>

      <td className="p-2">
        <QuickEdit
          editable={canEdit}
          label={t('taskList.changeProgress', { key: task.key })}
          width="w-56"
          content={
            <span className="flex items-center gap-1.5" title={`${task.progressPct}%`}>
              <span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted" aria-hidden>
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${task.progressPct}%` }}
                />
              </span>
              {/* Con số đứng cạnh thanh: một thanh cao một pixel rưỡi không đọc
                  ra 20% hay 30%, và màu không phải kênh thông tin duy nhất. */}
              <span className="w-8 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {task.progressPct}%
              </span>
            </span>
          }
        >
          {() => (
            /*
              Dùng lại **đúng** thứ board đang dùng — APM-LIST-d.

              `ProgressQuickAction` đã mang sẵn cả ba luật khó: có checklist thì
              `%` là kết quả của việc tích từng mục nên chỉ đọc; việc ở "cần
              làm" hiện % nhưng không sửa; và thanh trượt *là* thanh tiến độ chứ
              không phải hai thanh nói cùng một số. Viết lại một ô nhập số ở đây
              là bỏ cả ba, rồi chúng sẽ trôi khỏi nhau.
            */
            <ProgressQuickAction
              value={task.progressPct}
              checklist={task.checklist ?? null}
              // Chỉ "đang làm" mới sửa được — "xong" cũng khoá (`TASK_NOT_IN_PROGRESS`).
              notStarted={task.statusCategory !== 'in_progress'}
              inBacklog={task.inBacklog === true}
              onChange={onProgress}
            />
          )}
        </QuickEdit>
      </td>

      <td className="p-0">
        <FillCell
          tone={priority}
          label={t(`priority.${task.priority}`)}
          // Ưu tiên là lập kế hoạch như hạn — chỉ PM; API chặn ở `assertMaySchedule`.
          editable={canSchedule}
          menuLabel={t('taskList.changePriority')}
          options={PRIORITIES.map((value) => ({
            value,
            label: t(`priority.${value}`),
            tone: priorityToneOf(value),
          }))}
          current={task.priority}
          onPick={(value) => onPriority(value as TaskPriority)}
        />
      </td>

      {/* Bấm xem nguồn SP và ước lượng lại — người dùng yêu cầu 2026-11-02. */}
      <td className="p-2 text-right text-xs tabular-nums">
        <StoryPointBadge task={task} />
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ô bấm-để-sửa dùng chung — APM-LIST-d.
 *
 * ## Vì sao một component chứ không ba
 *
 * Hạn, Timeline và Tiến độ đều là cùng một cử chỉ: bấm vào ô, một khối nhỏ bung
 * ra ngay dưới, sửa xong thì đóng. Ba bản chép tay của cùng cử chỉ ấy sẽ trôi
 * khỏi nhau ở đúng chỗ khó thấy nhất — chỗ đóng mở — và bản quên sửa là bản
 * người dùng phải bấm hai lần mới đóng được.
 *
 * ## Đóng bằng nền bắt click, không bằng `onBlur`
 *
 * `onBlur` bắn **trước** `onClick` của thứ bên trong, nên khối đóng mất trước
 * khi cú bấm tới nơi. Đây là lỗi kinh điển của mọi popover tự viết, và nó đã
 * xuất hiện hai lần trong repo này (`TaskCombobox`, `FillCell`).
 *
 * ## `children` là hàm, nhận `close`
 *
 * Nội dung bên trong quyết định lúc nào là xong: chọn một ngày thì đóng ngay,
 * còn kéo thanh tiến độ thì **không** — kéo là một chuỗi thao tác, và đóng ở
 * bước đầu tiên là cướp mất phần còn lại.
 */
function QuickEdit({
  content,
  label,
  editable,
  width = 'w-48',
  children,
}: {
  readonly content: ReactNode;
  readonly label: string;
  readonly editable: boolean;
  readonly width?: string;
  readonly children: (close: () => void) => ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);

  // Không sửa được thì **không phải** một nút: một nút bị tắt mời người ta thử
  // bấm rồi không hiểu vì sao không được.
  if (!editable) return content;

  return (
    <span className="relative inline-block">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
        className="rounded-control px-1 py-0.5 text-left hover:bg-muted"
      >
        {content}
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <span
            role="dialog"
            aria-label={label}
            className={cn(
              'glass-popup-sm absolute left-0 top-full z-50 mt-1 block rounded-DEFAULT border border-border bg-background p-2 shadow-md',
              width,
            )}
          >
            {children(() => setOpen(false))}
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * Ô **tô kín màu**, bấm để đổi giá trị — APM-LIST-a.
 *
 * ⚠️ Màu vào `style`, **không** vào `className`: giá trị đến từ CSDL, và
 * Tailwind quét mã nguồn nên một lớp ghép lúc chạy sẽ không tồn tại — ô hiện
 * đúng chỗ, đúng kích thước, và không màu.
 *
 * Không có quyền sửa thì nó là một `<span>`, không phải một `<button>` bị tắt:
 * một nút tắt mời người ta thử bấm rồi không hiểu vì sao không được, còn một ô
 * không phải nút thì không hứa gì cả.
 */
function FillCell({
  tone,
  label,
  editable,
  menuLabel,
  options,
  current,
  onPick,
}: {
  readonly tone: { background: string; foreground: string };
  readonly label: string;
  readonly editable: boolean;
  readonly menuLabel: string;
  readonly options: readonly {
    value: string;
    label: string;
    tone: { background: string; foreground: string };
    /** Tiêu đề nhóm — vẽ một lần ở đầu mỗi dãy mục liền nhau cùng nhóm. */
    group?: string;
  }[];
  readonly current: string;
  readonly onPick: (value: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const style = { backgroundColor: tone.background, color: tone.foreground };

  if (!editable) {
    return (
      <span
        className="flex h-10 items-center justify-center px-2 text-xs font-medium"
        style={style}
      >
        {label}
      </span>
    );
  }

  return (
    <span className="relative block">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menuLabel}
        onClick={() => setOpen((value) => !value)}
        // `brightness` chứ không đổi màu nền: nền là dữ liệu, và một màu hover
        // cố định sẽ xoá mất chính thứ ô này đang nói.
        className="flex h-10 w-full items-center justify-center px-2 text-xs font-medium transition-[filter] hover:brightness-95"
        style={style}
      >
        {label}
      </button>

      {open ? (
        <>
          {/* Nền bắt click để đóng: `onBlur` bắn **trước** `onClick` của mục
              trong menu, nên danh sách đóng mất trước khi cú bấm tới nơi. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />

          <span
            role="menu"
            aria-label={menuLabel}
            className="absolute left-1/2 top-full z-50 mt-1 flex w-44 -translate-x-1/2 flex-col gap-0.5 rounded-DEFAULT border border-border bg-background p-1 shadow-md"
          >
            {options.map((option, index) => (
              <Fragment key={option.value}>
                {option.group !== undefined && option.group !== options[index - 1]?.group ? (
                  <span
                    role="presentation"
                    className="truncate px-1.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground first:pt-0.5"
                  >
                    {option.group}
                  </span>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    if (option.value !== current) onPick(option.value);
                  }}
                  className="flex items-center gap-2 rounded-DEFAULT px-1.5 py-1 text-left text-xs hover:bg-muted"
                >
                  <span
                    aria-hidden
                    className="size-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: option.tone.background }}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.value === current ? (
                    <Check aria-hidden className="size-3.5 shrink-0 text-primary" />
                  ) : null}
                </button>
              </Fragment>
            ))}
          </span>
        </>
      ) : null}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function BulkBar({
  count,
  columns,
  outsideLabel = null,
  sprintLabel,
  onStatus,
  onClear,
}: {
  readonly count: number;
  readonly columns: readonly WorkflowStatusView[];
  /** Khác `null` ⇒ board có hai bộ cột cùng tên; chia `<optgroup>` để phân biệt. */
  readonly outsideLabel?: string | null;
  readonly sprintLabel: string;
  readonly onStatus: (statusId: string) => void;
  readonly onClear: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const inSprint = columns.filter((column) => column.sprintId !== null);
  const outside = columns.filter((column) => column.sprintId === null);

  return (
    <div className="sticky bottom-2 z-30 flex flex-wrap items-center gap-2 self-center rounded-DEFAULT border border-border bg-background px-3 py-2 shadow-lg">
      <span className="text-sm font-medium tabular-nums">{t('taskList.picked', { count })}</span>

      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {t('taskList.changeStatus')}
        <select
          className="h-8 rounded-control border border-border-strong bg-background px-2 text-xs"
          value=""
          onChange={(event) => {
            if (event.target.value !== '') onStatus(event.target.value);
          }}
        >
          <option value="" disabled>
            {t('taskList.pickStatus')}
          </option>
          {outsideLabel !== null && inSprint.length > 0 ? (
            <>
              <optgroup label={sprintLabel}>
                {inSprint.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label={outsideLabel}>
                {outside.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.name}
                  </option>
                ))}
              </optgroup>
            </>
          ) : (
            columns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.name}
              </option>
            ))
          )}
        </select>
      </label>

      <Button size="sm" variant="ghost" onClick={onClear}>
        <X aria-hidden className="size-3.5" />
        {t('taskList.clearPick')}
      </Button>
    </div>
  );
}
